import { useEffect, useRef, useState, FormEvent } from 'react';
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, Link, Stack, TextField, Typography } from '@mui/material';
import { authService } from '../../shared/services/auth.service';
import { usersService } from '../../shared/services/users.service';
import { useAuth } from '../../shared/hooks/useAuth';
import { useApiError } from '../../shared/errors/useApiError';
import { useNotification } from '../../shared/notifications/NotificationProvider';
import {
  SplitAuthLayout,
  MarketingBenefits,
} from '../../shared/components/layout/SplitAuthLayout';
import { validators, normalisePhone } from '../../shared/validators/auth-validators';

const RESEND_COOLDOWN_SECONDS = 60;

export function VerifyPhonePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated, isLoading, patchUser } = useAuth();
  const { translate } = useApiError();
  const { notify } = useNotification();

  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldownLeft, setCooldownLeft] = useState(0);
  // The phone step has a server-side gate: a code must be issued at least once
  // before the user can verify. We request it on mount — but only once.
  const initialRequestSent = useRef(false);

  // ── Inline phone-number edit ─────────────────────────────────────────
  // The user lands here right after registering — the phone they typed in
  // the form might have a typo. Letting them fix it without going back to
  // the sign-up flow avoids a frustrating dead-end.
  const [editingPhone, setEditingPhone] = useState(false);
  const [newPhone, setNewPhone] = useState('');
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [savingPhone, setSavingPhone] = useState(false);

  const nextParam = searchParams.get('next');
  const safeNext = nextParam && nextParam.startsWith('/') ? nextParam : '/dashboard';

  useEffect(() => {
    if (isLoading) return;
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
      return;
    }
    // Sequential gate: phone step is only available once email is verified.
    if (user && !user.emailVerified) {
      navigate(`/verify-email?next=${encodeURIComponent(safeNext)}`, { replace: true });
      return;
    }
    if (user?.phoneVerified) {
      navigate(safeNext, { replace: true });
      return;
    }
    // Fire the first SMS automatically — the user expects to receive a code on
    // arrival. The backend's resend cooldown protects against accidental
    // double-fires when React re-mounts the page in StrictMode.
    if (user?.emailVerified && !user.phoneVerified && !initialRequestSent.current) {
      initialRequestSent.current = true;
      authService
        .requestPhoneVerification()
        .then(() => setCooldownLeft(RESEND_COOLDOWN_SECONDS))
        .catch(() => {
          // Swallow — the user can retry via the resend button. Hitting the
          // cooldown on a fresh load just means a code is already in flight.
        });
    }
  }, [isAuthenticated, isLoading, user, navigate, safeNext]);

  useEffect(() => {
    if (cooldownLeft <= 0) return;
    const id = setInterval(() => setCooldownLeft((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [cooldownLeft]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const err = validators.verificationCode(code.trim());
    if (err) {
      setCodeError(t(err));
      return;
    }
    setCodeError(null);
    setLoading(true);
    try {
      await authService.verifyPhone(code.trim());
      patchUser({ phoneVerified: true });
      notify.success(t('auth.verify.phoneSuccess'));
      navigate(safeNext, { replace: true });
    } catch (e2) {
      notify.error(translate(e2));
    } finally {
      setLoading(false);
    }
  };

  const onResend = async () => {
    setResending(true);
    try {
      await authService.requestPhoneVerification();
      setCooldownLeft(RESEND_COOLDOWN_SECONDS);
      notify.success(t('auth.verify.resentPhone'));
    } catch (e) {
      notify.error(translate(e));
    } finally {
      setResending(false);
    }
  };

  const startEditingPhone = () => {
    setNewPhone(user?.phoneNumber ?? '');
    setPhoneError(null);
    setEditingPhone(true);
  };

  const cancelEditingPhone = () => {
    setEditingPhone(false);
    setPhoneError(null);
  };

  const savePhone = async () => {
    const validationKey = validators.phone(newPhone);
    if (validationKey) {
      setPhoneError(t(validationKey));
      return;
    }
    const normalised = normalisePhone(newPhone);
    if (normalised === user?.phoneNumber) {
      // Nothing to do — just collapse the editor.
      setEditingPhone(false);
      return;
    }
    setPhoneError(null);
    setSavingPhone(true);
    try {
      const updated = await usersService.updateMe({ phoneNumber: normalised });
      // Refresh the cached auth user so the subtitle line + AuthGuard see
      // the new number immediately. phoneVerified was reset by the backend.
      patchUser({
        phoneNumber: updated.phoneNumber ?? undefined,
        phoneVerified: updated.phoneVerified,
      });
      // Issue a fresh code to the NEW number. Reset the local input/cooldown.
      await authService.requestPhoneVerification();
      setCode('');
      setCodeError(null);
      setCooldownLeft(RESEND_COOLDOWN_SECONDS);
      setEditingPhone(false);
      notify.success(t('auth.verify.phoneUpdatedSentNewCode'));
    } catch (e) {
      notify.error(translate(e));
    } finally {
      setSavingPhone(false);
    }
  };

  return (
    <SplitAuthLayout
      variant="customer"
      marketing={
        <Box>
          <Typography variant="h3" sx={{ fontWeight: 800, lineHeight: 1.15, mb: 2 }}>
            {t('auth.verify.marketingTitle')}
          </Typography>
          <Typography variant="subtitle1" sx={{ color: 'inherit', opacity: 0.92 }}>
            {t('auth.verify.marketingSubtitle')}
          </Typography>
          <MarketingBenefits
            items={[
              t('auth.verify.marketingPoint1'),
              t('auth.verify.marketingPoint2'),
              t('auth.verify.marketingPoint3'),
            ]}
          />
        </Box>
      }
    >
      <Typography variant="h3" sx={{ mb: 1 }}>
        {t('auth.verify.phoneTitle')}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: editingPhone ? 1 : 0.5 }}>
        {t('auth.verify.phoneSubtitle', { phone: user?.phoneNumber ?? '' })}
      </Typography>

      {/* Inline phone editor — collapsed by default, expanded when the user
          clicks "Wrong number?" so they can fix a typo without losing the
          session. Saving triggers a fresh code on the new number. */}
      {!editingPhone ? (
        <Box sx={{ mb: 3 }}>
          <Link
            component="button"
            type="button"
            onClick={startEditingPhone}
            sx={{ fontWeight: 600 }}
          >
            {t('auth.verify.wrongPhoneAction')}
          </Link>
        </Box>
      ) : (
        <Box
          sx={{
            p: 2,
            mb: 3,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
            {t('auth.verify.editPhoneTitle')}
          </Typography>
          <TextField
            label={t('auth.phone')}
            value={newPhone}
            onChange={(e) => {
              setNewPhone(e.target.value);
              setPhoneError(null);
            }}
            fullWidth
            autoFocus
            autoComplete="tel"
            placeholder="+33 6 00 00 00 00"
            error={!!phoneError}
            helperText={phoneError || t('auth.phoneHint')}
            disabled={savingPhone}
          />
          <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
            <Button
              onClick={savePhone}
              variant="contained"
              disabled={savingPhone}
            >
              {savingPhone ? t('common.loading') : t('auth.verify.editPhoneSave')}
            </Button>
            <Button onClick={cancelEditingPhone} disabled={savingPhone}>
              {t('common.cancel')}
            </Button>
          </Stack>
        </Box>
      )}

      <Alert severity="info" sx={{ mb: 3 }}>
        {t('auth.verify.stepIndicator', { step: 2, total: 2 })}
      </Alert>

      <Box component="form" onSubmit={onSubmit} noValidate>
        <Stack spacing={2}>
          <TextField
            label={t('auth.verify.codeLabel')}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            required
            autoFocus
            fullWidth
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="123456"
            error={!!codeError}
            helperText={codeError || t('auth.verify.codeHint')}
            inputProps={{ maxLength: 6, pattern: '\\d{6}' }}
          />

          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={loading}
            fullWidth
            sx={{ py: 1.5 }}
          >
            {loading ? t('common.loading') : t('auth.verify.submit')}
          </Button>

          <Button
            onClick={onResend}
            disabled={resending || cooldownLeft > 0}
            variant="text"
          >
            {cooldownLeft > 0
              ? t('auth.verify.resendInSeconds', { seconds: cooldownLeft })
              : t('auth.verify.resend')}
          </Button>
        </Stack>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 3, textAlign: 'center' }}>
        <Link component={RouterLink} to="/login" sx={{ color: 'primary.main', fontWeight: 600 }}>
          {t('auth.backToLogin')}
        </Link>
      </Typography>
    </SplitAuthLayout>
  );
}
